import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton, SpecraInput, SpecraSelect, SpecraText } from '@mono/ui-components/ensigns';
import { SpecraFormField, SpecraPopover } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { Plus } from 'lucide-react';
import { useRef, useState } from 'react';
function newOperationId() {
    return typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `op-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}
/** "+ New draft": name + "Start from" → `CreateDraft` (WRT-04 §4). */ export function NewDraftPopover({ disabled, onCreate, recentVersions }) {
    const [anchor, setAnchor] = useState(null);
    const [open, setOpen] = useState(false);
    const [name, setName] = useState('');
    const [from, setFrom] = useState('');
    const [error, setError] = useState(null);
    const [pending, setPending] = useState(false);
    const operationId = useRef('');
    const inFlight = useRef(false);
    const openPopover = (next)=>{
        if (next) {
            operationId.current = newOperationId();
            setName('');
            setFrom('');
            setError(null);
        }
        setOpen(next);
    };
    const submit = async (e)=>{
        e?.preventDefault();
        if (inFlight.current || !name.trim()) return;
        inFlight.current = true;
        setPending(true);
        setError(null);
        try {
            await onCreate({
                name: name.trim(),
                fromVersionId: from,
                operationId: operationId.current
            });
            setOpen(false);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'The draft could not be created.');
        } finally{
            inFlight.current = false;
            setPending(false);
        }
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(SpecraButton, {
                "data-testid": Testids.officeWorkspace.newDraft,
                disabled: disabled,
                iconLeft: /*#__PURE__*/ _jsx(Plus, {
                    "aria-hidden": "true",
                    className: "h-4 w-4"
                }),
                onClick: ()=>openPopover(!open),
                ref: setAnchor,
                size: "sm",
                variant: "ghost",
                children: "New draft"
            }),
            /*#__PURE__*/ _jsx(SpecraPopover, {
                anchor: anchor,
                "aria-label": "New draft",
                onOpenChange: openPopover,
                open: open,
                children: /*#__PURE__*/ _jsxs("form", {
                    className: "flex w-72 flex-col gap-[var(--space-3)]",
                    onSubmit: submit,
                    children: [
                        /*#__PURE__*/ _jsx(SpecraText, {
                            size: "caption",
                            tone: "muted",
                            children: "A draft is a separate copy of the document. Only the main draft goes into the package."
                        }),
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: "Name",
                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                autoFocus: true,
                                "data-testid": Testids.officeWorkspace.newDraftName,
                                id: "office-new-draft-name",
                                onChange: (e)=>setName(e.target.value),
                                placeholder: "e.g. Numbers-first",
                                value: name
                            })
                        }),
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: "Start from",
                            children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                                "data-testid": Testids.officeWorkspace.newDraftFrom,
                                id: "office-new-draft-from",
                                onChange: (e)=>setFrom(e.target.value),
                                size: "sm",
                                value: from,
                                children: [
                                    /*#__PURE__*/ _jsx("option", {
                                        value: "",
                                        children: "Current version"
                                    }),
                                    recentVersions.map((v)=>/*#__PURE__*/ _jsx("option", {
                                            value: v.id,
                                            children: `Version v${v.versionNumber}${v.label ? ` · ${v.label}` : ''}`
                                        }, v.id))
                                ]
                            })
                        }),
                        error ? /*#__PURE__*/ _jsx(SpecraText, {
                            role: "alert",
                            size: "caption",
                            tone: "danger",
                            children: error
                        }) : null,
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex justify-end gap-[var(--space-2)]",
                            children: [
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    onClick: ()=>setOpen(false),
                                    size: "sm",
                                    type: "button",
                                    variant: "ghost",
                                    children: "Cancel"
                                }),
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    "data-testid": Testids.officeWorkspace.newDraftSubmit,
                                    disabled: !name.trim() || pending,
                                    isLoading: pending,
                                    size: "sm",
                                    type: "submit",
                                    children: "Create"
                                })
                            ]
                        })
                    ]
                })
            })
        ]
    });
}
